import React, {useEffect, useState, useMemo} from "react";
import {
    AppBar, Toolbar, Container, Typography, Button,
    Box, FormControlLabel, Switch,
    Snackbar, Alert, Tooltip, CircularProgress
} from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import {initializeApp} from "firebase/app";
import {getAuth, signInWithCustomToken, onAuthStateChanged} from "firebase/auth";
import {getFunctions, httpsCallable, connectFunctionsEmulator} from "firebase/functions";
import LogoutIcon from "@mui/icons-material/Logout";
import {CodeInput} from "./components/code-input";
import {TalentTable} from "./components/talent-dashboard";

const firebaseConfig = {
    apiKey: "AIzaSyDrX0peq7969LL_8uUt2hbYVM0N00nHLpc",
    authDomain: "agencypages-d0dc3.firebaseapp.com",
    projectId: "agencypages-d0dc3"
};

const app = initializeApp(firebaseConfig);
const functions = getFunctions(app, "europe-west1");

if (window.location.hostname === "localhost") {
    connectFunctionsEmulator(functions, "localhost", 5001); // default port for functions emulator
}

const buttonStyle = {
    borderRadius: '999px', // voll rund
    px: 4,                 // horizontale Padding
    py: 1.5,               // vertikale Padding
    fontWeight: 'bold',
    textTransform: 'none', // Groß-/Kleinschreibung beibehalten
};

const AgenciesPage = () => {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const [mode, setMode] = useState(() =>
        localStorage.getItem("theme") || (prefersDark ? "dark" : "light")
    );
    const [linkId, setLinkId] = useState(null);
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState("");
    const [verified, setVerified] = useState(false);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalError, setGlobalError] = useState(null);
    const [codeSentAt, setCodeSentAt] = useState(null);
    const [remainingTime, setRemainingTime] = useState(600);
    const [resendStatus, setResendStatus] = useState("idle");
    const [toast, setToast] = useState({open: false, message: "", severity: "info"});
    const [debugMode] = useState(false);

    const theme = useMemo(() =>
        createTheme({
            palette: {mode},
            components: {
                MuiCssBaseline: {
                    styleOverrides: {
                        body: {transition: "all 0.3s ease"}
                    }
                }
            }
        }), [mode]);

    const toggleMode = () => {
        const newMode = mode === "dark" ? "light" : "dark";
        setMode(newMode);
        localStorage.setItem("theme", newMode);
    };

    const showToast = (message, severity = "info") => {
        setToast({open: true, message, severity});
    };

    const fetchData = (id) => {
        const getData = httpsCallable(functions, "getAgencyPageData");
        getData({id: id ?? linkId})
            .then(res => {
                const fullContact = res.data?.contact || {};
                setData({
                    rows: res.data?.rows || [],
                    contact: {
                        name: fullContact.agent || '—',
                        email: fullContact.email || '—',
                    },
                    agency: res.data.agency,
                });
            })
            .catch(() => setGlobalError("Failed to load data."));
    };

    useEffect(() => {
        if (!codeSentAt) return;
        const interval = setInterval(() => {
            const secondsPassed = Math.floor((Date.now() - codeSentAt) / 1000);
            const remaining = 600 - secondsPassed;
            setRemainingTime(remaining);
            if (remaining <= 0) clearInterval(interval);
        }, 1000);
        return () => clearInterval(interval);
    }, [codeSentAt]);

    const handleResendCode = (id) => {
        const sendCode = httpsCallable(functions, "sendCode");
        setResendStatus("sending");
        sendCode({id: id ? id : linkId})
            .then(() => {
                setResendStatus("sent");
                setCodeSent(true);
                setCodeSentAt(Date.now());
                setRemainingTime(600);
                showToast("Code sent successfully", "success");
            })
            .catch(() => {
                setResendStatus("error");
                showToast("Failed to send code", "error");
            })
            .finally(() => {
                setTimeout(() => setResendStatus("idle"), 3000);
            });
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const id = params.get("id");
        setLinkId(id);

        if (!id) {
            setGlobalError("Missing ID in URL");
            return;
        }

        const auth = getAuth();
        const functions = getFunctions(app, "europe-west1");

        const sendCode = httpsCallable(functions, "sendCode");
        const skipSend = sessionStorage.getItem("justLoggedOut") === "true";
        if (skipSend) sessionStorage.removeItem("justLoggedOut");

        const unsub = onAuthStateChanged(auth, (user) => {
            const expectedUid = "linkuser_" + id;

            if (user?.uid === expectedUid) {
                setVerified(true);
                fetchData(id);
            } else {
                if (!skipSend) {
                    sendCode({id})
                        .then(() => {
                            setCodeSent(true);
                            setCodeSentAt(Date.now());
                            setRemainingTime(600);
                        })
                        .catch((err) => {
                            console.error("sendCode failed:", err);
                            setGlobalError("Failed to send code.");
                        });
                } else {
                    console.log("Skipped sendCode due to recent logout.");
                }
            }
        });

        return () => unsub();
    }, []);

    const handleVerify = (value) => {
        const codeToUse = value ?? code;
        setLoading(true);

        const verifyCode = httpsCallable(getFunctions(app, "europe-west1"), "verifyCode");
        verifyCode({id: linkId, code: codeToUse})
            .then(async (res) => {
                const token = res.data.token;
                const auth = getAuth(app);
                await signInWithCustomToken(auth, token);
                setVerified(true);
                fetchData();
                showToast("Verification successful", "success");
            })
            .catch(() => showToast("Verification failed", "error"))
            .finally(() => setLoading(false));
    };

    const savePrice = (data) => {

        const update = httpsCallable(functions, "updateAgencyPageData");

        update({
            id: linkId,
            addChannelInfo: data.addChannelInfo,
        })
            .then(() => {
            })
            .catch(() => {
            });
    };

    const suggestChannels = httpsCallable(functions, 'suggestChannels');

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline/>
            <Box sx={{display: 'flex', flexDirection: 'column', height: '100vh'}}>

                <AppBar
                    position="static"
                    elevation={0}
                    sx={{
                        bgcolor: mode === "light" ? "#f5f5f5" : "#121212",
                        borderBottom: '1px solid',
                        borderColor: mode === "light" ? '#ddd' : '#333',
                        py: 2,
                    }}
                >
                    <Toolbar sx={{ justifyContent: "space-between", alignItems: "center" }}>
                        <Box
                            component="img"
                            src={mode === "dark" ? "/assets/company_logo_light.png" : "/assets/company_logo_dark.png"}
                            alt="JMG Logo"
                            sx={{ height: 32 }}
                        />

                        {verified && (
                            <Button
                                variant="contained"
                                sx={{
                                    borderRadius: '999px',
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    bgcolor: "#000",
                                    color: "#fff",
                                    px: 4,
                                    py: 1.25,
                                    "&:hover": {
                                        bgcolor: "#222",
                                    },
                                }}
                                endIcon={<LogoutIcon />}
                            >
                                Save and Log Out
                            </Button>
                        )}
                    </Toolbar>
                </AppBar>


                <Container
                    sx={{mt: 4, mb: 8, flexGrow: 1, overflow: 'auto'}}
                >
                    {globalError && <Alert severity="error">{globalError}</Alert>}
                    {!codeSent && !verified && <Typography>Loading…</Typography>}

                    {codeSent && !verified && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="50vh"
                            gap={2}
                            textAlign="center">
                            <Typography>A code has been sent to your email. Please enter it below:</Typography>

                            <CodeInput onComplete={(code) => handleVerify(code)} margin="normal" autoFocus/>

                            <Box mt={2} display="flex" flexDirection={{xs: "column", sm: "row"}} gap={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="medium"
                                    onClick={handleVerify}
                                    disabled={loading || remainingTime <= 0}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit"/> : null}
                                >
                                    {loading ? "Verifying..." : "Verify"}
                                </Button>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="medium"
                                    onClick={handleResendCode}
                                    disabled={resendStatus === "sending"}
                                    startIcon={resendStatus === "sending" ?
                                        <CircularProgress size={20} color="inherit"/> : null}
                                >
                                    {resendStatus === "sending" ? "Sending…" : "Resend Code"}
                                </Button>
                            </Box>

                            <Box mt={2}>
                                {remainingTime > 0 ? (
                                    <Typography variant="body2" color="textSecondary">
                                        Code valid
                                        for: {Math.floor(remainingTime / 60)}:{String(remainingTime % 60).padStart(2, "0")} minutes
                                    </Typography>
                                ) : (
                                    <Typography color="error">Code expired. Please request a new one.</Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {verified && (
                        <>
                            <Box
                                display="flex"
                                flexDirection={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                mb={4}
                                gap={2}
                            >
                                <Box>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Hey, {data.contact?.name || '—'} from
                                    </Typography>
                                    <Typography variant="h4" fontWeight="bold">
                                        {data.agency?.name || '—'}
                                    </Typography>
                                </Box>

                                <Box maxWidth={420}>
                                    <Typography variant="body1">
                                        Here is all the information we have about your talents.
                                    </Typography>
                                    <Typography variant="body1" mt={1}>
                                        Please keep it up to date as much as possible to get the best offers and deals from our clients.
                                    </Typography>
                                </Box>
                            </Box>

                            <TalentTable
                                data={data.rows}
                                contact={data.contact}
                                savePrice={savePrice}
                                suggestChannels={suggestChannels}
                                agency={data.agency}
                            />
                        </>
                    )}

                    {debugMode && (
                        <Box mt={4} p={2} bgcolor="#eee" borderRadius={2}>
                            <Typography variant="subtitle2">Debug Info:</Typography>
                            <Typography variant="body2">Link ID: {linkId}</Typography>
                            <Typography variant="body2">Remaining time: {remainingTime}s</Typography>
                            <Typography variant="body2">Code sent
                                at: {codeSentAt ? new Date(codeSentAt).toLocaleString() : "n/a"}</Typography>
                            <Typography variant="body2">Verified: {verified ? "Yes" : "No"}</Typography>
                        </Box>
                    )}
                </Container>
            </Box>

            <Box
                position="fixed"
                bottom={16}
                right={16}
                left={16}
                zIndex={1300}
                bgcolor="background.paper"
                borderRadius={2}
                boxShadow={3}
                px={2}
                py={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left', flex: 1}}>
                    © 2025 All rights reserved JMG Brands and Talents GmbH · <a href="#"
                                                                                style={{color: 'inherit'}}>Imprint</a> · <a
                    href="#" style={{color: 'inherit'}}>Privacy Policy</a>
                </Typography>
                <FormControlLabel
                    control={<Switch checked={mode === "dark"} onChange={toggleMode}/>}
                    label={mode === "dark" ? "🌙" : "☀️"}
                />
            </Box>

            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast({...toast, open: false})}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert severity={toast.severity} sx={{width: "100%"}}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </ThemeProvider>
    );
};

export default AgenciesPage;
